/**
 * @file AccountInfoTab.js
 * @description This component displays the account information for an individual user with a two-column layout featuring editable settings and a read-only profile card.
 */

import {
    useState,
    useEffect,
    useContext,
    forwardRef,
    useImperativeHandle,
} from 'react';
import {
    createIndividual,
    updateIndividual,
    getUserPreferences,
    initiateUserOnboarding,
    updateCommunicationPreferences,
    resetUser,
    createTenantUser,
} from '../../services/individualService';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import { Dropdown } from '../helpers/Dropdown';
import style from './IndividualPage.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { hasSequentialChars } from '../../utils/utils';
import { useAuthContext } from '../../store/AuthContext';
import SecurityRoleDropdown from '../shared/asyncDropdowns/SecurityRoleDropdown';
import { sendForgotPasswordEmail } from '../../services/authService';
import Tooltip from '../ui/tooltips/Tooltip';
import { Avatar as MuiAvatar } from '@mui/material';
import PhoneInput from 'react-phone-number-input/input';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import MessageOptOut from './components/MessageOptOut';
import {
    SecurityAttributes,
    NOTIFICATION_METHOD,
    TenantUserStatusEnum,
    TenantUserType,
} from '../../utils/data/enums';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import PasswordInput from '../ui/PasswordInput';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import { parseISO } from 'date-fns';
import AccountDetailsDrawer from './components/AccountDetailsDrawer';
import { getText } from '../../i18n';

const getTenantUserStatus = statusId => {
    if (statusId === 0 || statusId === TenantUserStatusEnum.NotStarted)
        return 'Not Started';
    if (statusId === 1 || statusId === TenantUserStatusEnum.InvitationPending)
        return 'Invitation Pending';
    if (statusId === 2 || statusId === TenantUserStatusEnum.Pending)
        return 'Pending';
    if (statusId === 3 || statusId === TenantUserStatusEnum.Active)
        return 'Active';
    if (statusId === 4 || statusId === TenantUserStatusEnum.InActive)
        return 'Inactive';
    if (statusId === 5 || statusId === TenantUserStatusEnum.Disabled)
        return 'Disabled';
    if (statusId === 6 || statusId === TenantUserStatusEnum.Locked)
        return 'Locked';
    return 'Unknown';
};

const getCommunicationPreference = notificationMethodId => {
    if (notificationMethodId === NOTIFICATION_METHOD.primaryEmail)
        return 'Email';
    if (notificationMethodId === NOTIFICATION_METHOD.optionalEmail)
        return 'Email';
    if (notificationMethodId === NOTIFICATION_METHOD.primaryPhone) return 'SMS';
    if (notificationMethodId === NOTIFICATION_METHOD.optionalPhone)
        return 'SMS';
    if (notificationMethodId === NOTIFICATION_METHOD.pushNotification)
        return 'Push Notification';
    return '-';
};

const communicationTypeOptions = [
    { value: '', label: getText('msg_8598222918d3') },
    { value: 'SendRegistration', label: getText('msg_cab8cbeb1bda') },
    { value: 'PasswordReset', label: getText('msg_79167df1dd0b') },
];

const checkAccountDisabled = tenantUser => {
    if (tenantUser?.tenantUserStatusId === TenantUserStatusEnum.Disabled) {
        Toast.error(
            getText('msg_66aa8e9b0915'),
            {},
            getText('msg_8d884d6c8bcb'),
        );
        return true;
    }
    return false;
};

const AccountInfoTab = forwardRef(
    ({ individualId, individualRecord = null, onSave }, ref) => {
        const { showModal } = useContext(ModalContext);
        const [accountInfo, setAccountInfo] = useState({});
        const [isLoading, setIsLoading] = useState(true);
        const [tenantUser, setTenantUser] = useState(null);
        const [tenantUserPreferences, setTenantUserPreferences] =
            useState(null);
        const [populationGroups, setPopulationGroups] = useState([]);
        const [isSaving, setIsSaving] = useState(false);
        const [errors, setErrors] = useState({});
        const {
            companyCode,
            companyConfig,
            isSelfRegistrationEnabled,
            hasAttribute,
        } = useAuthContext();
        const { isAdminAudienceEnabled } = useAdminAudienceEnabled();
        const [communicationType, setCommunicationType] = useState('');
        const [isOnboardingCompleted, setIsOnboardingCompleted] =
            useState(false);
        const [newPassword, setNewPassword] = useState('');
        const [confirmPassword, setConfirmPassword] = useState('');
        const [isDrawerOpen, setIsDrawerOpen] = useState(false);
        const [passwordRequirements, setPasswordRequirements] = useState({
            length: false,
            uppercase: false,
            lowercase: false,
            number: false,
            special: false,
        });
        const [showPasswordRequirements, setShowPasswordRequirements] =
            useState(false);

        individualRecord?.tenantUsers?.forEach(tenantUserItem => {
            let accountAccessDateNoTime = null;

            if (tenantUserItem.accountAccessEndDate) {
                accountAccessDateNoTime =
                    tenantUserItem.accountAccessEndDate.substring(0, 10);
            }

            tenantUserItem.initialAccountAccessEndDate =
                accountAccessDateNoTime;
            tenantUserItem.accountAccessEndDate = accountAccessDateNoTime;
        });

        useEffect(() => {
            const isCompleted =
                tenantUser?.tenantUserStatusId ===
                    TenantUserStatusEnum.Active ||
                tenantUser?.tenantUserStatusId ===
                    TenantUserStatusEnum.InActive;
            setIsOnboardingCompleted(isCompleted);
        }, [tenantUser?.tenantUserStatusId]);

        useEffect(() => {
            const loadPopulationGroups = async () => {
                try {
                    const groups = await fetchPopulationGroups();
                    setPopulationGroups([
                        { value: '', label: getText('msg_8598222918d3') },
                        ...groups.map(group => ({
                            value: group.id,
                            label: group.name,
                        })),
                    ]);
                } catch (error) {
                    console.error('Error fetching population groups:', error);
                    Toast.error(getText('msg_854b792bbb3e'));
                }
            };

            loadPopulationGroups();
        }, []);

        useEffect(() => {
            const fetchUserPreferences = async () => {
                try {
                    setIsLoading(true);
                    const preferences = await getUserPreferences(
                        individualId,
                        tenantUser?.externalId,
                    );
                    setTenantUserPreferences(preferences);
                } catch (error) {
                    console.error('Error fetching user preferences:', error);
                    Toast.error(getText('msg_e434781fe6a7'));
                } finally {
                    setIsLoading(false);
                }
            };

            if (tenantUser?.externalId) {
                fetchUserPreferences();
            }
        }, [tenantUser, individualId]);

        useEffect(() => {
            setAccountInfo({
                ...(tenantUser || {}),
                dataFilterId: individualRecord?.dataFilterId ?? null,
            });
        }, [tenantUser, individualRecord?.dataFilterId]);

        useEffect(() => {
            const [tenantUsers] = individualRecord?.tenantUsers ?? [];
            setTenantUser(tenantUsers || null);
        }, [individualRecord]);

        const onCommunicationTypeChange = selectedOption => {
            setCommunicationType(selectedOption);
        };

        const handleAdminCheckboxChange = () => {
            const isCurrentlyClientAdmin =
                accountInfo?.tenantUserTypeId == TenantUserType.ClientAdmin;
            const nextTenantUserTypeId = isCurrentlyClientAdmin
                ? TenantUserType.Individual
                : TenantUserType.ClientAdmin;

            setAccountInfo(prev => ({
                ...prev,
                securityRoleId: null,
                tenantUserTypeId: nextTenantUserTypeId,
                dataFilterId:
                    nextTenantUserTypeId == TenantUserType.ClientAdmin
                        ? prev.dataFilterId
                        : null,
            }));
        };

        const onSendCommunication = () => {
            if (checkAccountDisabled(tenantUser)) return;

            if (!communicationType || communicationType === '') {
                Toast.error(getText('msg_3a427d8b2747'));
                return;
            }

            if (communicationType === 'PasswordReset') {
                if (!isOnboardingCompleted) {
                    Toast.info(getText('msg_f2786d63e5cb'));
                    return;
                }
                showModal({
                    type: ModalTypes.ACTION,
                    title: '',
                    subtitle: getText('msg_2a294eb413ad'),
                    text: '',
                    onYes: handleInitiatePasswordReset,
                    onNo: () => {},
                    noButtonText: getText('msg_77dfd2135f4d'),
                    yesButtonText: getText('msg_9bc2575c3930'),
                });
                return;
            }

            if (communicationType === 'SendRegistration') {
                if (tenantUser) {
                    showModal({
                        type: ModalTypes.ACTION,
                        subtitle: getText('msg_14a7e24d601f'),
                        text: getText('msg_e11a3ca64fa7'),
                        onYes: handleInitiateOnboarding,
                        onNo: () => {},
                        noButtonText: getText('msg_77dfd2135f4d'),
                        yesButtonText: getText('msg_9bc2575c3930'),
                    });
                    return;
                }
                handleInitiateOnboarding();
            }
        };

        const handlePhoneChange = value => {
            if (
                !value &&
                tenantUserPreferences?.notificationPreferredMethodId ===
                    NOTIFICATION_METHOD.optionalPhone
            ) {
                Toast.error(getText('msg_88da0712db10'));
                return;
            }

            setAccountInfo(prevInfo => ({
                ...prevInfo,
                mobilePhoneNumber: value,
            }));

            if (value && !isPossiblePhoneNumber(value)) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    mobilePhoneNumber: 'Invalid phone number',
                }));
                return;
            }

            setErrors(prevErrors => ({
                ...prevErrors,
                mobilePhoneNumber: null,
            }));
        };

        const handleInputChange = event => {
            const { name, value } = event.target;

            if (name === 'emailAddress') {
                if (
                    !value &&
                    tenantUserPreferences?.notificationPreferredMethodId ===
                        NOTIFICATION_METHOD.optionalEmail
                ) {
                    Toast.error(getText('msg_c097df247dab'));
                    return;
                }
            }

            setAccountInfo(prevInfo => ({
                ...prevInfo,
                [name]: value,
            }));

            if (errors[name]) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    [name]: null,
                }));
            }
        };

        const handleAccountStatusToggle = () => {
            if (hasAttribute(SecurityAttributes.IndividualReadOnly)) return;

            const currentStatusType = getTenantUserStatus(
                tenantUser?.tenantUserStatusId,
            );
            const isCurrentlyDisabled = currentStatusType === 'Disabled';
            const nextAction = isCurrentlyDisabled ? 'enable' : 'disable';

            showModal({
                type: ModalTypes.ACTION,
                title: `You are about to ${nextAction} this account`,
                subtitle: getText('msg_91f4c1a2c077'),
                text: isCurrentlyDisabled
                    ? 'The user will be able to login to their account again.'
                    : 'The user will be unable to login or access this account',
                onYes: () => {
                    const newStatusId = isCurrentlyDisabled
                        ? TenantUserStatusEnum.Active
                        : TenantUserStatusEnum.Disabled;

                    handleSave({
                        tenantUserStatusId: newStatusId,
                    });
                },
                onNo: () => {},
                noButtonText: getText('msg_816c52fd2bdd'),
                yesButtonText: getText('msg_5397e0583f14'),
            });
        };

        const handleResetAccount = () => {
            if (hasAttribute(SecurityAttributes.IndividualReadOnly)) return;

            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_8cc227914db7'),
                subtitle: getText('msg_80197bf075cd'),
                text: getText('msg_55511b9fcbd1'),
                onYes: async () => {
                    try {
                        setIsSaving(true);
                        await resetUser(individualId, tenantUser?.externalId);
                        Toast.success(getText('msg_111f87eb566f'));
                        onSave();
                    } catch (error) {
                        console.error('Error resetting account:', error);
                        Toast.error(getText('msg_b754a3dfc9fc'));
                    } finally {
                        setIsSaving(false);
                    }
                },
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: getText('msg_8cc227914db7'),
            });
        };

        const handleToggleLockAccount = () => {
            if (hasAttribute(SecurityAttributes.IndividualReadOnly)) return;

            const currentStatusType = getTenantUserStatus(
                tenantUser?.tenantUserStatusId,
            );
            const isCurrentlyLocked = currentStatusType === 'Locked';
            const action = isCurrentlyLocked ? 'unlock' : 'lock';

            showModal({
                type: ModalTypes.ACTION,
                title: `${isCurrentlyLocked ? 'Unlock' : 'Lock'} Account`,
                subtitle: `Are you sure you want to ${action} this account?`,
                text: isCurrentlyLocked
                    ? 'This will allow the user to access their account again.'
                    : 'This will prevent the user from accessing their account.',
                onYes: () => {
                    const newStatusId = isCurrentlyLocked
                        ? TenantUserStatusEnum.Active
                        : TenantUserStatusEnum.Locked;

                    handleSave({ tenantUserStatusId: newStatusId });
                },
                onNo: () => {},
                noButtonText: getText('msg_77dfd2135f4d'),
                yesButtonText: isCurrentlyLocked ? 'Unlock' : 'Lock',
            });
        };

        useImperativeHandle(ref, () => ({
            handleSave,
            hasPassword: () => newPassword && newPassword.trim() !== '',
        }));

        const validatePassword = password => {
            setPasswordRequirements({
                length:
                    password.length >=
                        companyConfig.application?.password_min_length &&
                    password.length <=
                        companyConfig.application?.password_max_length,
                uppercase: /[A-Z]/.test(password),
                lowercase: /[a-z]/.test(password),
                number: /\d/.test(password),
                special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
                repeats: !/(.)\1{3}/.test(password),
                sequential: !hasSequentialChars(password),
                patterns: !/(.{1,2})\1\1/.test(password),
            });
        };

        const handlePasswordChange = event => {
            const { value } = event.target;
            setNewPassword(value);

            if (value) {
                validatePassword(value);
            }

            if (confirmPassword && value !== confirmPassword) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    confirmPassword: 'Passwords do not match',
                }));
                return;
            }

            if (confirmPassword) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    confirmPassword: null,
                }));
            }
        };

        const handleConfirmPasswordChange = event => {
            const { value } = event.target;
            setConfirmPassword(value);

            if (value && value !== newPassword) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    confirmPassword: 'Passwords do not match',
                }));
                return;
            }

            setErrors(prevErrors => ({
                ...prevErrors,
                confirmPassword: null,
            }));
        };

        const handlePasswordFocus = () => {
            setShowPasswordRequirements(true);
        };

        const handlePasswordBlur = () => {
            if (newPassword && newPassword.trim() !== '') return;
            setShowPasswordRequirements(false);
        };

        const renderPasswordRequirement = (requirement, text) => {
            const isMet = passwordRequirements[requirement];
            const getClassName = () => {
                if (!newPassword) return 'text-muted';
                if (isMet) return 'text-success';
                return 'text-danger';
            };

            return (
                <small
                    className={`password-requirement d-block w-100 ${getClassName()}`}
                >
                    {newPassword && (
                        <i
                            className={`bi ${isMet ? 'bi-check' : 'bi-x'} me-2`}
                        />
                    )}
                    {text}
                </small>
            );
        };

        const validateBeforeSave = () => {
            const validationErrors = {};

            if (
                accountInfo?.tenantUserTypeId == TenantUserType.ClientAdmin &&
                accountInfo?.securityRoleId == null
            ) {
                validationErrors.securityRole =
                    'Security role must be provided for client admins';
            }

            if (
                tenantUserPreferences?.notificationPreferredMethodId ===
                    NOTIFICATION_METHOD.optionalEmail &&
                !accountInfo?.emailAddress
            ) {
                validationErrors.emailAddress =
                    "Email is required as it's your preferred notification method";
                Toast.error(getText('msg_c8c170bdddad'));
                return false;
            }

            if (
                tenantUserPreferences?.notificationPreferredMethodId ===
                    NOTIFICATION_METHOD.optionalPhone &&
                !accountInfo?.mobilePhoneNumber
            ) {
                validationErrors.mobilePhoneNumber =
                    "Phone number is required as it's your preferred notification method";
                Toast.error(getText('msg_88da0712db10'));
                return false;
            }

            if (
                (newPassword || confirmPassword) &&
                newPassword !== confirmPassword
            ) {
                validationErrors.confirmPassword = 'Passwords do not match';
            }

            if (
                accountInfo?.accountAccessEndDate &&
                accountInfo.accountAccessEndDate !==
                    accountInfo.initialAccountAccessEndDate
            ) {
                const today = new Date();
                const currentDate = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate(),
                );
                const comparisonDate = parseISO(
                    accountInfo.accountAccessEndDate,
                );

                if (comparisonDate < currentDate) {
                    Toast.error(getText('msg_927175d081b3'));
                    return false;
                }
            }

            if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                return false;
            }

            return true;
        };

        const handleSave = async (payloadOverrides = {}) => {
            try {
                const isChangingAccountStatus =
                    'tenantUserStatusId' in payloadOverrides;
                const isSettingPassword =
                    newPassword && newPassword.trim() !== '';

                if (
                    !isChangingAccountStatus &&
                    !isSettingPassword &&
                    checkAccountDisabled(tenantUser)
                ) {
                    return;
                }

                if (!validateBeforeSave()) return;

                setIsLoading(true);
                setIsSaving(true);

                const nextTenantUserTypeId =
                    payloadOverrides.tenantUserTypeId ??
                    accountInfo?.tenantUserTypeId ??
                    TenantUserType.Individual;

                const nextDataFilterId =
                    nextTenantUserTypeId == TenantUserType.ClientAdmin
                        ? (payloadOverrides.dataFilterId ??
                          accountInfo?.dataFilterId)
                        : null;

                const shouldSaveIndividualDataFilter =
                    nextDataFilterId !== individualRecord?.dataFilterId;

                const preferencesPayload = {
                    id: tenantUserPreferences?.id,
                    notificationPreferredMethodId:
                        payloadOverrides.notificationPreferredMethodId ??
                        tenantUserPreferences?.notificationPreferredMethodId,
                    emailAddress:
                        payloadOverrides.emailAddress ??
                        (accountInfo?.emailAddress || null),
                    phoneNumber:
                        payloadOverrides.phoneNumber ??
                        (accountInfo?.mobilePhoneNumber || null),
                    fileEmailAddress: tenantUserPreferences?.fileEmailAddress,
                    filePhoneNumber: tenantUserPreferences?.filePhoneNumber,
                    notificationCategoryOptOuts:
                        tenantUserPreferences?.notificationCategoryOptOuts ||
                        [],
                    securityRoleId:
                        payloadOverrides.securityRoleId ??
                        accountInfo?.securityRoleId,
                    tenantUserStatusId:
                        payloadOverrides.tenantUserStatusId ??
                        accountInfo?.tenantUserStatusId,
                    tenantUserTypeId: nextTenantUserTypeId,
                    accountAccessEndDate: accountInfo?.accountAccessEndDate,
                };

                if (newPassword && newPassword === confirmPassword) {
                    preferencesPayload.password = newPassword;
                    preferencesPayload.tenantUserStatusId =
                        TenantUserStatusEnum.Active;
                }

                if (shouldSaveIndividualDataFilter) {
                    const individualPayload = {
                        ...individualRecord,
                        dataFilterId: nextDataFilterId,
                    };

                    if (individualPayload.id != null || individualId != null) {
                        await updateIndividual(individualPayload);
                    } else {
                        await createIndividual(individualPayload);
                    }
                }

                if (!tenantUser) {
                    await createTenantUser(individualId, preferencesPayload);
                } else {
                    await updateCommunicationPreferences(
                        individualId,
                        tenantUser.externalId,
                        preferencesPayload,
                    );
                }

                if (newPassword) {
                    setNewPassword('');
                    setConfirmPassword('');
                }

                setTenantUser(prev => ({
                    ...prev,
                    tenantUserStatusId: preferencesPayload.tenantUserStatusId,
                }));

                setTenantUserPreferences(prev => ({
                    ...prev,
                    notificationPreferredMethodId:
                        preferencesPayload.notificationPreferredMethodId,
                }));

                setAccountInfo(prevInfo => ({
                    ...prevInfo,
                    emailAddress: preferencesPayload.emailAddress,
                    mobilePhoneNumber: preferencesPayload.phoneNumber,
                    dataFilterId: nextDataFilterId,
                    tenantUserStatusId: preferencesPayload.tenantUserStatusId,
                }));

                Toast.success(getText('msg_b101aa603e02'));
                onSave();
            } catch (error) {
                console.error('Unable to save changes', error);
                if (error?.response?.data?.detail) {
                    Toast.error(error.response.data.detail);
                } else {
                    Toast.error(getText('msg_3c2ce6f3c1a9'));
                }
            } finally {
                setIsSaving(false);
                setIsLoading(false);
            }
        };

        const handleRoleChange = selectedId => {
            const newRoleId = selectedId == 0 ? null : selectedId;

            setAccountInfo(prevInfo => ({
                ...prevInfo,
                securityRoleId: newRoleId,
            }));

            if (newRoleId != null)
                setErrors(prevErrors => ({
                    ...prevErrors,
                    securityRole: null,
                }));
        };

        const handleAccountAccessEndDateChange = accountAccessEndDate => {
            setAccountInfo(prevInfo => ({
                ...prevInfo,
                accountAccessEndDate,
            }));
        };

        const handleAudienceChange = dataFilterId => {
            setAccountInfo(prevInfo => ({
                ...prevInfo,
                dataFilterId: dataFilterId || null,
            }));
        };

        const handleInitiateOnboarding = async () => {
            if (checkAccountDisabled(tenantUser)) return;

            const email =
                individualRecord?.emailAddress ||
                tenantUserPreferences?.fileEmailAddress ||
                null;

            const mobile =
                tenantUserPreferences?.phoneNumber ||
                tenantUserPreferences?.filePhoneNumber ||
                tenantUser?.mobilePhoneNumber ||
                accountInfo?.mobilePhoneNumber ||
                individualRecord?.mobilePhoneNumber ||
                null;

            try {
                await initiateUserOnboarding(individualId, email, mobile);
                Toast.success(getText('msg_4598cd4a9f73'));
                onSave();
            } catch (error) {
                console.error('Failed to initiate onboarding:', error);
                Toast.error(getText('msg_efc695333371'));
            }
        };

        const handleInitiatePasswordReset = async () => {
            if (checkAccountDisabled(tenantUser)) return;

            try {
                setIsSaving(true);

                const preferredMethod =
                    tenantUserPreferences?.notificationPreferredMethodId;
                const isPhonePreferred = [
                    NOTIFICATION_METHOD.primaryPhone,
                    NOTIFICATION_METHOD.optionalPhone,
                ].includes(preferredMethod);

                if (isPhonePreferred) {
                    const phoneNumber =
                        tenantUserPreferences?.phoneNumber ||
                        tenantUserPreferences?.filePhoneNumber;

                    if (!phoneNumber) {
                        Toast.error(
                            getText('msg_2b3ea0ab0418'),
                            {},
                            getText('msg_cb4ce1779d01'),
                        );
                        return;
                    }

                    await sendForgotPasswordEmail(companyCode, phoneNumber);
                    Toast.success(getText('msg_2dd210f35bcd'));
                    return;
                }

                const emailAddress =
                    tenantUserPreferences?.emailAddress ||
                    tenantUserPreferences?.fileEmailAddress ||
                    tenantUser?.emailAddress ||
                    individualRecord?.emailAddress;

                if (!emailAddress) {
                    Toast.error(getText('msg_ed34efd4e3f2'));
                    return;
                }

                await sendForgotPasswordEmail(companyCode, emailAddress);
                Toast.success(getText('msg_d6c15d94e522'));
            } catch (error) {
                console.error('Unable to request password reset', error);
                if (error?.response?.data?.detail) {
                    Toast.error(error.response.data.detail);
                } else {
                    Toast.error(getText('msg_b6ba53994a96'));
                }
            } finally {
                setIsSaving(false);
            }
        };

        const openDrawer = () => {
            setIsDrawerOpen(true);
        };

        const closeDrawer = () => {
            setIsDrawerOpen(false);
        };

        const isClientAdmin =
            accountInfo?.tenantUserTypeId === TenantUserType.ClientAdmin;
        const isReadOnly = hasAttribute(SecurityAttributes.IndividualReadOnly);
        const canManageAudience = hasAttribute(
            SecurityAttributes.ManageAudience,
        );
        const canShowAudienceControl = isAdminAudienceEnabled;

        const renderAccountControls = () => {
            const currentStatus = tenantUser?.tenantUserStatusId;
            const currentStatusType = getTenantUserStatus(currentStatus);
            const isDisabled = currentStatusType === 'Disabled';
            const isLocked = currentStatusType === 'Locked';

            return (
                <div className="d-flex align-items-center flex-wrap gap-3 mb-4">
                    <div className="form-switch">
                        <input
                            id="accountDisabled"
                            className="form-check-input custom-switch-input"
                            type="checkbox"
                            checked={!isDisabled}
                            disabled={isReadOnly}
                            name="accountDisabled"
                            onChange={handleAccountStatusToggle}
                        />
                        <label
                            onClick={
                                isReadOnly ? null : handleAccountStatusToggle
                            }
                            className={`form-check-label custom-switch-label ${style.toggleBtn} px-2 text-nowrap`}
                            htmlFor="accountDisabled"
                        >
                            {getText('msg_18a83b21ac9d')}
                        </label>
                    </div>

                    <div className="form-switch">
                        <input
                            id="adminCheckbox"
                            className="form-check-input custom-switch-input"
                            type="checkbox"
                            checked={isClientAdmin}
                            disabled={isReadOnly}
                            name="adminCheckbox"
                            onChange={handleAdminCheckboxChange}
                        />
                        <label
                            onClick={
                                isReadOnly ? null : handleAdminCheckboxChange
                            }
                            className={`form-check-label custom-switch-label ${style.toggleBtn} px-2 text-nowrap`}
                            htmlFor="adminCheckbox"
                        >
                            {getText('msg_4e7afebcfbae')}
                        </label>
                    </div>

                    {!isReadOnly &&
                        [
                            'Active',
                            'InActive',
                            'Pending',
                            'Disabled',
                            'Locked',
                            'NotStarted',
                        ].includes(currentStatusType) &&
                        !isSelfRegistrationEnabled && (
                            <p
                                type="button"
                                onClick={handleResetAccount}
                                className="ing-text-buttons d-inline m-0"
                            >
                                <i className="bi bi-arrow-clockwise me-2" />
                                {getText('msg_8cc227914db7')}
                            </p>
                        )}

                    {!isReadOnly && isLocked && (
                        <p
                            type="button"
                            onClick={handleToggleLockAccount}
                            className="ing-text-buttons d-inline m-0"
                        >
                            <i className="bi bi-unlock me-2" />
                            {getText('msg_739f0f24eb91')}
                        </p>
                    )}
                </div>
            );
        };

        const renderProfileCard = () => {
            const fullName =
                `${individualRecord?.firstName || ''} ${individualRecord?.lastName || ''}`.trim();
            const initials = `${individualRecord?.firstName?.[0] || ''}${individualRecord?.lastName?.[0] || ''}`;

            return (
                <div className={style.profileCard}>
                    <div className={style.profileCardContent}>
                        <MuiAvatar
                            sx={{
                                width: 220,
                                height: 220,
                                fontSize: '2.5rem',
                                marginBottom: '16px',
                            }}
                            src={accountInfo?.avatarUrl}
                        >
                            {initials}
                        </MuiAvatar>

                        <h5 className={style.profileName}>{fullName}</h5>

                        <button
                            type="button"
                            className={style.viewPopulationGroupsLink}
                            onClick={openDrawer}
                        >
                            {getText('msg_0cd1399d00ce')}
                        </button>

                        <div className={style.profileDetailsContainer}>
                            <div className={style.profileDetailsRow}>
                                <div className={style.profileDetailItem}>
                                    <span className={style.profileDetailLabel}>
                                        {getText('msg_6df7d8380ffc')}
                                    </span>
                                    <span className={style.profileDetailValue}>
                                        {accountInfo?.preferredName ||
                                            individualRecord?.firstName ||
                                            '-'}
                                    </span>
                                </div>

                                <div className={style.profileDetailItem}>
                                    <span className={style.profileDetailLabel}>
                                        {getText('msg_8a2abafdb920')}
                                    </span>
                                    <span className={style.profileDetailValue}>
                                        {accountInfo?.pronouns || '-'}
                                    </span>
                                </div>
                            </div>

                            <div className={style.profileDetailItem}>
                                <span className={style.profileDetailLabel}>
                                    {getText('msg_89b86ab0e66f')}
                                </span>
                                <span className={style.profileDetailValue}>
                                    {tenantUserPreferences?.locale ||
                                        'English-US'}
                                </span>
                            </div>

                            <div className={style.profileDetailItem}>
                                <span className={style.profileDetailLabel}>
                                    {getText('msg_88d7dacc35f7')}
                                </span>
                                <span className={style.profileDetailValue}>
                                    {getCommunicationPreference(
                                        tenantUserPreferences?.notificationPreferredMethodId,
                                    )}
                                </span>
                            </div>

                            <div className={style.profileDetailItem}>
                                <span className={style.profileDetailLabel}>
                                    {getText('msg_22860b2a8893')}
                                </span>
                                <MessageOptOut
                                    preferences={tenantUserPreferences}
                                    isLoading={isLoading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        const renderPasswordRequirements = () => {
            if (!showPasswordRequirements) return null;

            return (
                <div className="password-requirements mt-2">
                    {renderPasswordRequirement(
                        'length',
                        `Between ${companyConfig.application?.password_min_length}-${companyConfig.application?.password_max_length} characters`,
                    )}
                    {renderPasswordRequirement(
                        'uppercase',
                        'Contains at least 1 uppercase letter',
                    )}
                    {renderPasswordRequirement(
                        'lowercase',
                        'Contains at least 1 lowercase letter',
                    )}
                    {renderPasswordRequirement(
                        'number',
                        'Contains at least 1 number',
                    )}
                    {renderPasswordRequirement(
                        'special',
                        'Contains at least 1 special character',
                    )}
                    {!companyConfig.application
                        ?.password_allow_duplicate_values &&
                        renderPasswordRequirement(
                            'repeats',
                            'No 4 consecutive repeating characters',
                        )}
                    {!companyConfig.application
                        ?.password_allow_duplicate_values &&
                        renderPasswordRequirement(
                            'sequential',
                            'No 4 sequential characters',
                        )}
                    {!companyConfig.application
                        ?.password_allow_duplicate_values &&
                        renderPasswordRequirement(
                            'patterns',
                            'No repeating patterns',
                        )}
                </div>
            );
        };

        if (isLoading && tenantUser) {
            return <Spinner width={50} className={style.loadingSpinner} />;
        }

        const selectedAudienceLabel =
            populationGroups.find(
                option => option.value === accountInfo?.dataFilterId,
            )?.label || 'Select';

        return (
            <>
                <div
                    className={`d-flex justify-content-center ${style.accountInfoContainer}`}
                >
                    {/* Left Column - Editable Settings */}
                    <div className="col-12 col-sm-12 col-md-12 col-lg-7">
                        {!tenantUser && (
                            <div className="mb-4">
                                <p className="text-muted">
                                    {getText('msg_d5f2f775a70f')}
                                </p>
                            </div>
                        )}

                        {tenantUser && renderAccountControls()}

                        {/* Admin, Security Role, Account Access End Date */}
                        <div className="row g-2 g-md-3 mb-3 align-items-center justify-content-xl-between">
                            <div className="col-12 col-xl-4">
                                <SecurityRoleDropdown
                                    className={`${errors.securityRole ? 'is-invalid' : ''}`}
                                    selectedRoleId={accountInfo?.securityRoleId}
                                    onRoleChange={handleRoleChange}
                                    disabled={isReadOnly || !isClientAdmin}
                                    isRequired={false}
                                    isMultiTenant={false}
                                    inlineLabel={false}
                                    showSelectedItemsInField={true}
                                    showSelectionBox={false}
                                    showSelectedCount={true}
                                />
                                {errors.securityRole && (
                                    <div className="invalid-feedback">
                                        {errors.securityRole}
                                    </div>
                                )}
                            </div>

                            {canShowAudienceControl && (
                                <div className="col-12 col-xl-4 mb-3">
                                    <label
                                        htmlFor="audience"
                                        className="ms-1 mb-2 d-block white-space-nowrap"
                                    >
                                        {getText('msg_51d9934555e2')}
                                    </label>
                                    <div className="w-100">
                                        <Dropdown
                                            defaultValue={selectedAudienceLabel}
                                            options={populationGroups}
                                            onChange={handleAudienceChange}
                                            disabled={
                                                isReadOnly ||
                                                !isClientAdmin ||
                                                !canManageAudience
                                            }
                                            allowSearch={true}
                                        />
                                    </div>
                                </div>
                            )}

                            {tenantUser && (
                                <div className="col-12 col-xl-4 mb-3">
                                    <label
                                        htmlFor="accountAccessEndDate"
                                        className="ms-1 mb-2 d-block white-space-nowrap"
                                    >
                                        {getText('msg_84b147812589')}
                                        <Tooltip
                                            html="Set an optional end date for account access. After this date, the user will no longer be able to access their account."
                                            placement="right"
                                        >
                                            <i
                                                className="bi bi-info-circle"
                                                style={{
                                                    paddingLeft: '5px',
                                                    fontSize: '13px',
                                                    color: 'var(--iNGAGED-Primary-Color)',
                                                }}
                                            />
                                        </Tooltip>
                                    </label>
                                    <div className="w-100">
                                        <CustomDatePicker
                                            id="accountAccessEndDate"
                                            selectedDate={
                                                tenantUser?.accountAccessEndDate
                                            }
                                            onDateChange={
                                                handleAccountAccessEndDateChange
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Contact Information */}
                        {tenantUser && (
                            <>
                                <div className="mb-3">
                                    <label
                                        htmlFor="optionalEmail"
                                        className="form-label"
                                    >
                                        <i className="bi bi-envelope me-2" />
                                        {getText('msg_e602020c60b7')}
                                        {tenantUserPreferences?.notificationPreferredMethodId ===
                                            NOTIFICATION_METHOD.optionalEmail && (
                                            <Tooltip
                                                html="This email is the preferred notification method and cannot be removed"
                                                placement="right"
                                            >
                                                <i
                                                    className="bi bi-asterisk"
                                                    style={{
                                                        paddingLeft: '5px',
                                                        fontSize: '10px',
                                                        color: 'red',
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </label>
                                    <input
                                        id="optionalEmail"
                                        placeholder={getText(
                                            'msg_0c6c4102d4df',
                                        )}
                                        type="email"
                                        maxLength="50"
                                        className={`form-control ${errors.emailAddress ? 'is-invalid' : ''}`}
                                        value={accountInfo?.emailAddress || ''}
                                        onChange={handleInputChange}
                                        name="emailAddress"
                                    />
                                    {errors.emailAddress && (
                                        <div className="invalid-feedback">
                                            {errors.emailAddress}
                                        </div>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <label
                                        htmlFor="optionalPhone"
                                        className="form-label"
                                    >
                                        <i className="bi bi-telephone me-2" />
                                        {getText('msg_2604e454517e')}
                                        {tenantUserPreferences?.notificationPreferredMethodId ===
                                            NOTIFICATION_METHOD.optionalPhone}
                                    </label>
                                    <PhoneInput
                                        country="US"
                                        id="optionalPhone"
                                        placeholder={getText(
                                            'msg_0c6c4102d4df',
                                        )}
                                        className={`form-control ${errors.mobilePhoneNumber ? 'is-invalid' : ''}`}
                                        value={
                                            accountInfo?.mobilePhoneNumber || ''
                                        }
                                        onChange={handlePhoneChange}
                                        name="mobilePhoneNumber"
                                    />
                                    {errors.mobilePhoneNumber && (
                                        <div className="invalid-feedback">
                                            {errors.mobilePhoneNumber}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Send Communication */}
                        <div className="mb-4">
                            <label
                                htmlFor="communicationType"
                                className="form-label"
                            >
                                {getText('msg_f0ce7ab678f1')}
                            </label>
                            <div className="d-flex gap-2">
                                <div className="flex-grow-1">
                                    <Dropdown
                                        name="communicationType"
                                        defaultValue="Select"
                                        options={communicationTypeOptions}
                                        onChange={onCommunicationTypeChange}
                                        value={communicationType}
                                        disabled={hasAttribute(
                                            SecurityAttributes.IndividualReadOnly,
                                        )}
                                    />
                                </div>
                                {!hasAttribute(
                                    SecurityAttributes.IndividualReadOnly,
                                ) && (
                                    <button
                                        onClick={onSendCommunication}
                                        type="button"
                                        className="button-base ing-button-primary-left px-4 align-self-center"
                                        disabled={
                                            !communicationType ||
                                            (communicationType ===
                                                'PasswordReset' &&
                                                !isOnboardingCompleted)
                                        }
                                    >
                                        {getText('msg_9bc2575c3930')}
                                    </button>
                                )}
                            </div>
                            {communicationType === 'PasswordReset' &&
                                !isOnboardingCompleted && (
                                    <div className="text-danger mt-1">
                                        <small>
                                            {getText('msg_fee5a70f0e9f')}
                                        </small>
                                    </div>
                                )}
                        </div>

                        {/* Password Management */}
                        <div className="row align-items-baseline">
                            <div className="col-12 col-md-6 mb-3">
                                <label
                                    htmlFor="newPassword"
                                    className="form-label"
                                >
                                    {getText('msg_208bb0d762ab')}
                                    <Tooltip
                                        html="Setting a new password will immediately update the user's credentials. The user will need to use this new password on their next login."
                                        placement="right"
                                    >
                                        <i
                                            className="bi bi-info-circle"
                                            style={{
                                                paddingLeft: '5px',
                                                fontSize: '13px',
                                                color: 'var(--iNGAGED-Primary-Color)',
                                            }}
                                        />
                                    </Tooltip>
                                </label>
                                <PasswordInput
                                    id="newPassword"
                                    name="newPassword"
                                    value={newPassword}
                                    onChange={handlePasswordChange}
                                    onFocus={handlePasswordFocus}
                                    onBlur={handlePasswordBlur}
                                    placeholder={getText('msg_a8b10891fe35')}
                                    errorMessage={errors.newPassword}
                                    disabled={hasAttribute(
                                        SecurityAttributes.IndividualReadOnly,
                                    )}
                                />
                                {renderPasswordRequirements()}
                            </div>

                            <div className="col-12 col-md-6 mb-3">
                                <label
                                    htmlFor="confirmPassword"
                                    className="form-label"
                                >
                                    {getText('msg_378e8b36fd0b')}
                                </label>
                                <PasswordInput
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={handleConfirmPasswordChange}
                                    placeholder={getText('msg_8b8d6abf91f7')}
                                    errorMessage={null}
                                    disabled={hasAttribute(
                                        SecurityAttributes.IndividualReadOnly,
                                    )}
                                    isConfirmField={true}
                                    originalPassword={newPassword}
                                    showMatchFeedback={true}
                                />
                            </div>
                        </div>
                    </div>

                    {tenantUser && (
                        <div
                            className={`col-12 col-sm-12 col-md-12 col-lg-4 ${style.profileCardContainer}`}
                        >
                            {renderProfileCard()}
                        </div>
                    )}
                </div>
                <AccountDetailsDrawer
                    isOpen={isDrawerOpen}
                    closeDrawer={closeDrawer}
                    individualId={individualId}
                />
            </>
        );
    },
);

export default AccountInfoTab;
